import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AssignmentForm = ({ 
  selectedTechnicians, 
  primaryTechnician,
  onSubmit, 
  onCancel,
  availableTickets 
}) => {
  const [formData, setFormData] = useState({
    ticketId: '',
    scheduledDate: '',
    scheduledTime: '',
    estimatedDuration: '2',
    priority: 'medium',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const durationOptions = [
    { value: '1', label: '1 hour' },
    { value: '2', label: '2 hours' },
    { value: '3', label: '3 hours' },
    { value: '4', label: '4 hours' },
    { value: '6', label: '6 hours' },
    { value: '8', label: '8 hours' }
  ];

  const ticketOptions = availableTickets?.map(ticket => ({
    value: ticket?.id,
    label: `${ticket?.id} - ${ticket?.customer}`,
    description: ticket?.issue
  }));

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.ticketId) {
      newErrors.ticketId = 'Please select a ticket';
    }

    if (!formData?.scheduledDate) {
      newErrors.scheduledDate = 'Please select a date';
    }

    if (!formData?.scheduledTime) {
      newErrors.scheduledTime = 'Please select a time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSubmit({
        ...formData,
        technicians: selectedTechnicians,
        primaryTechnician: primaryTechnician
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          Create Assignment
        </h2>
        <button
          onClick={onCancel}
          className="p-2 rounded-lg hover:bg-muted transition-smooth"
        >
          <Icon name="X" size={20} color="var(--color-foreground)" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-primary/5 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-medium text-foreground mb-3">Selected Technicians</h3>
          <div className="space-y-2">
            {selectedTechnicians?.map((tech) => (
              <div key={tech?.id} className="flex items-center justify-between">
                <span className="text-sm text-foreground">{tech?.name}</span>
                {tech?.id === primaryTechnician?.id && (
                  <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded">
                    Primary
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <Select
          label="Select Ticket"
          required
          searchable
          options={ticketOptions}
          value={formData?.ticketId}
          onChange={(value) => handleChange('ticketId', value)}
          error={errors?.ticketId}
          placeholder="Search and select ticket..."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="date"
            label="Scheduled Date"
            required
            value={formData?.scheduledDate}
            onChange={(e) => handleChange('scheduledDate', e?.target?.value)}
            error={errors?.scheduledDate}
            min={new Date()?.toISOString()?.split('T')?.[0]}
          />

          <Input
            type="time"
            label="Scheduled Time"
            required
            value={formData?.scheduledTime}
            onChange={(e) => handleChange('scheduledTime', e?.target?.value)}
            error={errors?.scheduledTime}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Estimated Duration"
            options={durationOptions}
            value={formData?.estimatedDuration}
            onChange={(value) => handleChange('estimatedDuration', value)}
          />

          <Select
            label="Priority Level"
            options={priorityOptions}
            value={formData?.priority}
            onChange={(value) => handleChange('priority', value)}
          />
        </div>

        <Input
          type="text"
          label="Assignment Notes"
          placeholder="Add any special instructions or notes..."
          value={formData?.notes}
          onChange={(e) => handleChange('notes', e?.target?.value)}
          description="Optional notes for the technician team"
        />

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            fullWidth
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            fullWidth
            iconName="Check"
            iconPosition="left"
          >
            Create Assignment
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AssignmentForm;